'use client';
import { Fragment, useEffect, useState } from 'react';
import {
  Listbox,
  Transition,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from '@headlessui/react';
import { ChevronDownIcon, RedCheckIcon } from '@/icons';
import { twMerge } from 'tailwind-merge';
import Svg from '../Svg';

type DropDownListProps = {
  id: string;
  placeholder: string;
  options: OptionType[];
  setValue?: any;
  trigger?: any;
  defaultSelectedValue?: OptionType;
  error?: any;
  disabled?: boolean;
  allowValueChange?: boolean;
  valueToChange?: string;
};

type OptionType = any;

export default function DropDownList({
  id,
  placeholder,
  options,
  setValue,
  trigger,
  defaultSelectedValue,
  error,
  disabled = false,
  allowValueChange,
  valueToChange,
  ...rest
}: DropDownListProps) {
  const [selected, setSelected] = useState<OptionType | undefined>(
    defaultSelectedValue
  );

  useEffect(() => {
    if (allowValueChange) {
      const optionToSelect = options.find(item => {
        if (item && item.value === valueToChange) {
          return item;
        }
      });
      if (optionToSelect) {
        setSelected(optionToSelect);
      }
    }
  }, [valueToChange]);

  const handleSelect = (e: any) => {
    setSelected(e);
    if (setValue) setValue(id, e);
    if (trigger) trigger(id);
  };

  return (
    <div className='relative w-full'>
      <Listbox
        value={selected}
        onChange={handleSelect}
        disabled={disabled}
        {...rest}>
        <div className='font-gilroy w-full rounded-lg border border-black/20 placeholder:text-gray-500/80'>
          <ListboxButton
            className={twMerge(
              'relative w-full cursor-not-allowed rounded-lg bg-white py-3.5 pl-3 pr-10 text-left text-base opacity-50 focus:outline-hidden focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-700 sm:text-sm placeholder:text-base',
              error && 'outline-solid outline-1 outline-red-400',
              !disabled && 'cursor-pointer opacity-100'
            )}>
            <span
              className={twMerge(
                'block truncate text-base',
                !selected && 'text-base text-gray-500/80'
              )}>
              {selected ? selected?.name : placeholder}
            </span>
            <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
              <Svg viewBox='0 0 16 16' className='h-3 w-3'>
                <ChevronDownIcon />
              </Svg>
            </span>
          </ListboxButton>
          <Transition
            as={Fragment}
            leave='transition ease-in duration-100'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'>
            <ListboxOptions className='absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-black/20 bg-white py-1 text-base ring-1 ring-black/5 focus:outline-hidden sm:text-sm'>
              {options.map((el: any, elIdx: number) => (
                <ListboxOption
                  key={elIdx}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-3 pr-4 ${
                      active ? 'bg-[#e5e7eb] text-amber-900' : 'text-gray-900'
                    }`
                  }
                  value={el}>
                  {({ selected }) => (
                    <>
                      <span
                        className={`block truncate text-[#000] text-opacity-60 ${
                          selected ? 'font-medium' : 'font-normal'
                        }`}>
                        {el.name}
                      </span>
                      {selected ? (
                        <span className='absolute inset-y-0 left-0 flex items-center pl-3 text-fig'>
                          <RedCheckIcon />
                        </span>
                      ) : null}
                    </>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Transition>
        </div>
      </Listbox>
      {error && (
        <label htmlFor={id} className='block text-left text-xs text-error'>
          {error?.message}
        </label>
      )}
    </div>
  );
}
